/**
 * Serves demo responses by substituting the axios adapter.
 *
 * Why the adapter and not an interceptor: an interceptor cannot short-circuit
 * with a synthetic 200 without throwing a sentinel and unwinding it through the
 * response error handler, which already owns the 401/403 session logic in
 * `api/core.ts`. Tangling demo data into that path is how you end up signing
 * someone out because a fixture was missing. The adapter is axios's documented
 * substitution point, and it runs AFTER the request interceptor - so params
 * arrive already snake_cased, which is exactly the shape the generators read.
 *
 * Two properties this file is responsible for:
 *
 * 1. Demo responses are served ONLY when demo mode is explicitly on. The
 *    predicate is never "no auth token" - a signed-out visitor on a real page
 *    must get real errors, not fiction.
 * 2. An uncovered route falls through to the network rather than 404ing. A
 *    synthetic 404 would render an error state; falling through means the
 *    request behaves exactly as it does today (for an anonymous visitor: no
 *    org, so most calls never fire at all).
 */

import type { AxiosAdapter, AxiosInstance, InternalAxiosRequestConfig } from 'axios';
import { AxiosError, getAdapter } from 'axios';
import { useDemoModeStore } from '@/store/demoModeStore';
import { DEMO_NOT_FOUND, resolveDemoResponse, type DemoQuery } from './router';

/** Routes we saw but could not answer, so each is logged at most once. */
const reportedGaps = new Set<string>();

function isDemoActive(): boolean {
  // Read imperatively at request time: the adapter is installed once, but demo
  // mode is entered and left during the session.
  return useDemoModeStore.getState().isDemoMode;
}

export function withDemoAdapter(client: AxiosInstance): AxiosInstance {
  // A client with no `defaults` is not a real axios instance - it is a test
  // stub whose `get`/`post` are spies, so there is no transport to intercept
  // and nothing the demo could usefully answer. Bail rather than throw: this
  // runs inside `createApiClient`, so throwing here would break merely
  // IMPORTING any api module in those suites.
  if (!client?.defaults) return client;

  const configured = client.defaults.adapter;

  // Resolved on first request, not here.
  //
  // `client.defaults.adapter` is normally a NAME ('xhr' | 'http' | an array of
  // names) rather than a function, so it has to go through `getAdapter`. Doing
  // that eagerly means `withDemoAdapter` calls into axios at module-init time -
  // and three test files partially mock axios with `{...actual, ...}` spreads
  // that do not carry `getAdapter`, so merely importing any api module threw
  // and timed those suites out. Deferring costs nothing and keeps this wrapper
  // inert until something actually makes a request.
  let resolved: AxiosAdapter | null = null;
  const realAdapter = (config: InternalAxiosRequestConfig) => {
    resolved ??= typeof configured === 'function' ? configured : (getAdapter(configured) as AxiosAdapter);
    return resolved(config);
  };

  client.defaults.adapter = async (config: InternalAxiosRequestConfig) => {
    if (!isDemoActive()) return realAdapter(config);

    // Writes are never faked. A demo visitor cannot mutate anything anyway
    // (there is no session), and answering a POST with a synthetic 200 would
    // make the UI report success for something that did not happen.
    const method = (config.method ?? 'get').toLowerCase();
    if (method !== 'get') return realAdapter(config);

    const url = config.url ?? '';
    const payload = resolveDemoResponse(url, (config.params ?? {}) as DemoQuery);

    if (payload === undefined) {
      if (import.meta.env.DEV && !reportedGaps.has(url)) {
        reportedGaps.add(url);
        // Surfacing gaps in development is the whole reason fall-through is
        // silent in production: a missing fixture shows up as a console line
        // while building, not as a broken page in front of a prospect.
        console.debug(`[demo] no fixture for ${url} - falling through to the network`);
      }
      return realAdapter(config);
    }

    // A covered route whose answer is "no such thing" - e.g. a developer id
    // the fixture does not know. Rejecting locally lets the page reach its own
    // error state without six real requests leaving the browser first.
    if (payload === DEMO_NOT_FOUND) {
      const response = {
        data: { detail: 'Not found' },
        status: 404,
        statusText: 'Not Found',
        headers: {},
        config,
        request: null,
      };
      throw new AxiosError('Not found', AxiosError.ERR_BAD_REQUEST, config, null, response);
    }

    return {
      data: payload,
      status: 200,
      statusText: 'OK',
      headers: {},
      config,
      request: null,
    };
  };

  return client;
}

/** Test seam: the dev-only gap log is once-per-URL for the life of the module. */
export function __resetDemoAdapterGapLog(): void {
  reportedGaps.clear();
}
